import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Tracks per-book user state: feedback (like/dislike), read (true/false), and
// aggregate read stats. Backed by /api/feedback, /api/read, /api/read/stats.

function bookKey(title, author, isbn) {
    if (isbn) return `isbn:${isbn}`;
    const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
    return `ta:${norm(title)}|${norm(author)}`;
}

const ReadingContext = createContext(null);

export const ReadingProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const [feedback, setFeedback] = useState({}); // bookKey -> 'like' | 'dislike'
    const [readSet, setReadSet] = useState(new Set());
    const [stats, setStats] = useState({ count: 0, percentage: 0, total_books_in_world: 158000000 });
    const [ready, setReady] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const [fbRes, readRes, statsRes] = await Promise.all([
                api.get("/feedback/list"),
                api.get("/read/list"),
                api.get("/read/stats"),
            ]);
            const fb = {};
            for (const item of fbRes.data || []) {
                fb[item.book_key] = item.sentiment;
            }
            setFeedback(fb);
            const rs = new Set();
            for (const item of readRes.data || []) {
                rs.add(item.book_key);
            }
            setReadSet(rs);
            setStats(statsRes.data || stats);
        } catch {
            /* ignore — anon id may be missing on first paint */
        } finally {
            setReady(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (authLoading) return;
        refresh();
    }, [authLoading, user, refresh]);

    const setSentiment = async (book, sentiment) => {
        const key = bookKey(book.title, book.author, book.isbn);
        const current = feedback[key];
        // Toggle off if clicking the same one
        if (current === sentiment) {
            setFeedback((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            try {
                await api.delete("/feedback", {
                    params: {
                        title: book.title,
                        author: book.author,
                        isbn: book.isbn || undefined,
                    },
                });
            } catch {
                /* swallow */
            }
            return;
        }
        setFeedback((prev) => ({ ...prev, [key]: sentiment }));
        if (sentiment === "dislike") {
            toast.info(t("feedback.toast_dislike"));
        } else if (sentiment === "like") {
            toast.success(t("feedback.toast_like"));
        }
        try {
            await api.post("/feedback", {
                recommendation_id: book.recommendation_id,
                title: book.title,
                author: book.author,
                isbn: book.isbn || null,
                sentiment,
            });
        } catch {
            /* swallow — UI optimistic */
        }
    };

    const toggleRead = async (book) => {
        const key = bookKey(book.title, book.author, book.isbn);
        const wasRead = readSet.has(key);
        setReadSet((prev) => {
            const next = new Set(prev);
            if (wasRead) next.delete(key);
            else next.add(key);
            return next;
        });
        // Optimistic stats update
        setStats((s) => {
            const newCount = wasRead ? Math.max(0, s.count - 1) : s.count + 1;
            return {
                ...s,
                count: newCount,
                percentage: (newCount / (s.total_books_in_world || 158000000)) * 100,
            };
        });
        try {
            await api.post("/read", {
                title: book.title,
                author: book.author,
                isbn: book.isbn || null,
                read: !wasRead,
            });
        } catch {
            /* swallow */
        }
    };

    const getSentiment = (book) => feedback[bookKey(book.title, book.author, book.isbn)] || null;
    const isRead = (book) => readSet.has(bookKey(book.title, book.author, book.isbn));

    return (
        <ReadingContext.Provider
            value={{
                ready,
                feedback,
                stats,
                setSentiment,
                toggleRead,
                getSentiment,
                isRead,
                refresh,
            }}
        >
            {children}
        </ReadingContext.Provider>
    );
};

export const useReading = () => {
    const ctx = useContext(ReadingContext);
    if (!ctx) throw new Error("useReading must be used within ReadingProvider");
    return ctx;
};
